import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import TemplatedExperienceAnalyticsAPI from 'services/api/analytics.api';
import { TEALiveUsers } from 'interface/templated-experience/analytics.interface';
import { REFETCH_INTERVALS } from 'constants/shared';
import { getTenantId } from 'helpers/templated-experience.helper';

/**
 * useLiveUsers polls the near-realtime live-users endpoint on a short interval,
 * returning the current count plus the per-bucket activity series for the chart.
 * Auth + tenant are handled by the shared axios interceptor.
 */
export default function useLiveUsers(enabled = true) {
  const api = useMemo(() => new TemplatedExperienceAnalyticsAPI(), []);

  // Key by tenant so cached data never leaks across a tenant/auth switch (the
  // app keeps one QueryClient for the whole session).
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['live-users', getTenantId()],
    queryFn: () => api.getLiveUsers(),
    refetchInterval: REFETCH_INTERVALS.LIVE_USERS,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
  });

  const liveUsers: TEALiveUsers | undefined = data?.data;

  return { liveUsers, isLoading, isError, refetch };
}
